import Phaser from 'phaser';
import { BaseGameScene } from './BaseGameScene';
import { HEADER_HEIGHT } from '../config';
import { COLORS, DISPLAY_FONT, Radii } from '../ui/theme';
import { store } from '../state/store';
import { events } from '../state/events';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';
import { CoinIcon } from '../ui/CoinIcon';
import type { BatchStatus, SceneName } from '../types';

const PRICES = Array.from({ length: 20 }, (_, i) => String(i + 1));

const STATUS_STYLE: Record<BatchStatus, { label: string; color: number }> = {
  mixing: { label: 'MIXING', color: 0xd9a520 },
  baking: { label: 'BAKING', color: 0xf7901e },
  ready:  { label: 'READY',  color: 0x1f8b3a },
};

// Status board: what's been made (or is being made) today. Recipes are
// chosen in the Kitchen's recipe book — the menu just keeps score.
export class MenuScene extends BaseGameScene {
  private rowNodes: Phaser.GameObjects.GameObject[] = [];
  private unsubMenu?: () => void;

  constructor() {
    super({ key: 'Menu' });
  }

  protected sceneName(): Extract<SceneName, 'Menu' | 'Kitchen' | 'Oven' | 'Counter'> { return 'Menu'; }
  protected title(): string { return "TODAY'S MENU"; }
  protected backgroundColor(): number { return COLORS.bgMenu; }

  create(): void {
    this.setupChrome();
    this.renderRows();

    this.unsubMenu = events.on('menu:changed', () => this.renderRows());
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.unsubMenu?.());
  }

  private renderRows(): void {
    this.rowNodes.forEach((n) => n.destroy());
    this.rowNodes = [];
    const track = <T extends Phaser.GameObjects.GameObject>(n: T): T => {
      this.rowNodes.push(n);
      return n;
    };

    const w = this.scale.width;
    const h = this.scale.height;
    const cardW = 900;
    const cardX = w / 2 - cardW / 2;
    const cardY = HEADER_HEIGHT + 110;
    const cardH = h - cardY - 60;
    track(this.panel(cardX, cardY, cardW, cardH));

    const batches = store.state.todaysMenu;
    if (batches.length === 0) {
      track(this.add.text(w / 2, cardY + cardH / 2 - 40, 'Nothing on the menu yet!', {
        fontFamily: DISPLAY_FONT, fontSize: '30px', fontStyle: '600', color: '#9a7fd1',
      }).setOrigin(0.5));
      track(new Button(this, w / 2, cardY + cardH / 2 + 30, 'Go bake something →', {
        fill: COLORS.accentGreen, hoverFill: 0x5fe28b, fontSize: '24px', width: 320, height: 60,
        onClick: () => this.scene.start('Kitchen'),
      }));
      return;
    }

    const rowH = 84;
    batches.forEach((batch, i) => {
      const recipe = store.recipeById(batch.recipe);
      const y = cardY + 24 + i * (rowH + 10);
      const rowX = cardX + 20;
      const rowW = cardW - 40;

      const g = track(this.add.graphics());
      g.fillStyle(0xfff7ec, 1);
      g.fillRoundedRect(rowX, y, rowW, rowH, Radii.md);
      g.lineStyle(2, COLORS.accentOrange, 1);
      g.strokeRoundedRect(rowX, y, rowW, rowH, Radii.md);

      track(this.add.text(rowX + 24, y + rowH / 2, recipe?.name ?? batch.recipe, {
        fontFamily: DISPLAY_FONT, fontSize: '26px', fontStyle: '600', color: '#2a1f4a',
      }).setOrigin(0, 0.5));

      // Status chip
      const st = STATUS_STYLE[batch.status];
      const chipW = 130;
      const chipX = rowX + rowW - 24 - chipW;
      // Radius must be ≤ half the rect height — Phaser's rounded-rect
      // geometry breaks (stray full-height lines) when it exceeds it.
      const chip = track(this.add.graphics());
      chip.fillStyle(st.color, 1);
      chip.fillRoundedRect(chipX, y + rowH / 2 - 20, chipW, 40, 20);
      track(this.add.text(chipX + chipW / 2, y + rowH / 2, st.label, {
        fontFamily: DISPLAY_FONT, fontSize: '18px', fontStyle: '700', color: '#ffffff',
      }).setOrigin(0.5));

      // Price spinner: coin icon + 1..20 selector. setMenuPrice emits
      // menu:price (not menu:changed) so the row isn't rebuilt mid-click.
      const spinnerX = chipX - 100;
      track(new CoinIcon(this, spinnerX - 78, y + rowH / 2, { size: 34 }));
      track(new Spinner(this, spinnerX, y + rowH / 2, {
        values: PRICES,
        index: Math.max(0, Math.min(19, batch.price - 1)),
        width: 116,
        height: 48,
        fontSize: '22px',
        onChange: (v) => store.setMenuPrice(batch.id, Number(v)),
      }));

      // Middle detail: quantity when ready, temp/time otherwise
      const detail = batch.status === 'ready'
        ? `${batch.quantity} left`
        : `${recipe?.tempF ?? '?'}°F • ${recipe?.minutes ?? '?'} min`;
      track(this.add.text(spinnerX - 110, y + rowH / 2, detail, {
        fontFamily: DISPLAY_FONT, fontSize: '19px', color: '#55488a',
      }).setOrigin(1, 0.5));
    });
  }
}
